// main
import { useCopy } from "@/utils";
import { useAppTranslation } from "@/utils/useAppTranslation";
import type { SettlementFullDetails } from "@payping/api";
import { SettlementStatusEnum, TransferTypeEnum } from "@payping/api/app/api/cashOutTypes";
import { CuToman } from "@payping/icons";
import { getBankInfoFromSheba } from "@payping/utilities-shared/bank";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { toJalali } from "@payping/utils/dateUtils";
import { Bank, Descriptions, Drawer, Flex, LegacyCard, Typography } from "@payping/web-components";
import { ComponentProps } from "react";

type Props = { open: ComponentProps<typeof Drawer>["open"]; onClose: () => void; data: SettlementFullDetails };

const ReportSettlementDetails = (props: Props) => {
  const { t } = useAppTranslation();
  const { handleCopy } = useCopy();

  return (
    <Drawer
      placement="left"
      height={"100%"}
      open={props.open}
      onClose={() => props.onClose()}
      title={t("settlement.details.title")}
    >
      <Flex gap="32px" vertical>
        <Descriptions className="tw:w-full" bordered>
          <Descriptions.Item label={t("common.amount")}>
            {props.data.amount ? (
              <Flex align="center">
                <Typography.Text>
                  {formatLocalizedNumber({ value: props.data.amount ?? 0, options: { maximumFractionDigits: 2 } })}
                </Typography.Text>
                <CuToman width={24} height={24} />
              </Flex>
            ) : null}
          </Descriptions.Item>
        </Descriptions>
        {/* card */}
        <Flex className="tw:w-full" vertical gap="2px">
          <Flex justify="space-between" className="tw:w-full">
            <Typography.Text>{t("settlement.details.transferType.title")}</Typography.Text>
            <Typography.Text>
              {props.data.transferType
                ? t(`settlement.details.transferType.${TransferTypeEnum[props.data.transferType].toLowerCase()}`)
                : null}
            </Typography.Text>
          </Flex>
          <LegacyCard.Ant variant="outlined" className="tw:w-full">
            <Flex vertical className="tw:w-full" gap="8px">
              <Flex vertical className="tw:w-full" gap="2px">
                <Typography.Text>{props.data.fullName || ""}</Typography.Text>
                <Typography.Text>
                  {props.data.iban
                    ? `${!props.data.iban.toUpperCase().startsWith("IR") ? `IR${props.data.iban}` : props.data.iban}`
                    : null}
                </Typography.Text>
                <Flex gap="8px" onClick={() => handleCopy(props.data.iban)}>
                  {props.data.iban ? (
                    <Bank.Icon showTooltip bankName={getBankInfoFromSheba(props.data.iban)?.name} />
                  ) : null}
                  <Typography.Text className="color--gray500">{props.data.persianBankName}</Typography.Text>
                </Flex>
              </Flex>
              <Flex vertical className="color--gray500">
                <Flex justify="space-between">
                  <Typography.Text className="color--gray500">{t("nationalId")}</Typography.Text>
                  <Typography.Text className="color--gray500">{props.data.nationalId || ""}</Typography.Text>
                </Flex>
                <Flex justify="space-between">
                  <Typography.Text className="color--gray500">{t("mobile")}</Typography.Text>
                  <Typography.Text className="color--gray500">{props.data.mobile || ""}</Typography.Text>
                </Flex>
              </Flex>
            </Flex>
          </LegacyCard.Ant>
        </Flex>
        {/* settlement details  */}
        <Flex className="tw:w-full" vertical gap="2px">
          <Typography.Text>{t("settlement.details.title")}</Typography.Text>
          <Descriptions className="tw:w-full" bordered column={{ xxxl: 1, xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}>
            <Descriptions.Item label={t("settlement.details.code")}>
              <span onClick={() => handleCopy(props.data.code || "")}>{props.data.code || ""}</span>
            </Descriptions.Item>
            <Descriptions.Item label={t("description")}>{props.data.description || ""}</Descriptions.Item>
            <Descriptions.Item label={t("settlement.details.bankTransferReference")}>
              <span onClick={() => handleCopy(props.data.bankTransferReference || "")}>
                {props.data.bankTransferReference || ""}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={t("settlement.details.clientRefId")}>
              {props.data.clientRefId || ""}
            </Descriptions.Item>

            <Descriptions.Item label={t("common.status")}>
              {props.data.status
                ? t(`settlement.details.status.${SettlementStatusEnum[props.data.status].toLowerCase()}`)
                : null}
            </Descriptions.Item>
            <Descriptions.Item label={t("common.wage")}>
              {props.data.wage ? (
                <Flex align="center">
                  <Typography.Text type="secondary">
                    {formatLocalizedNumber({ value: props.data.wage, options: { maximumFractionDigits: 2 } })}
                  </Typography.Text>
                  <CuToman width="20px" height="20px" />
                </Flex>
              ) : null}
            </Descriptions.Item>

            <Descriptions.Item label={t("settlement.details.nearestACHTime")}>
              <Flex gap="4px" wrap="wrap">
                <Typography>
                  {props.data.nearestACHTime
                    ? `${toJalali({ date: props.data.nearestACHTime, toFormat: "HH:mm" }).string}`
                    : null}
                </Typography>
                <Typography>
                  {props.data.nearestACHTime
                    ? `${toJalali({ date: props.data.nearestACHTime, toFormat: "YYYY/MM/DD" }).string}`
                    : null}
                </Typography>
              </Flex>
            </Descriptions.Item>
          </Descriptions>
        </Flex>
        {/* request details */}
        <Flex className="tw:w-full" gap="2px" vertical>
          <Typography.Text>{t("settlement.details.requestDetails")}</Typography.Text>
          <Descriptions className="tw:w-full" bordered column={{ xxxl: 1, xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}>
            <Descriptions.Item label={t("settlement.details.requestCode")}>
              <span onClick={() => handleCopy(props.data.requestCode || "")}>{props.data.requestCode || ""}</span>
            </Descriptions.Item>

            <Descriptions.Item label={t("createDate")}>
              <Flex gap="4px" wrap="wrap">
                <Typography>
                  {props.data.createdOn
                    ? `${toJalali({ date: props.data.createdOn, toFormat: "HH:mm" }).string}`
                    : null}
                </Typography>
                <Typography>
                  {props.data.createdOn
                    ? `${toJalali({ date: props.data.createdOn, toFormat: "YYYY/MM/DD" }).string}`
                    : null}
                </Typography>
              </Flex>
            </Descriptions.Item>
            <Descriptions.Item label={t("settlement.details.submittedTime")}>
              <Flex gap="4px" wrap="wrap">
                <Typography>
                  {props.data.submittedTime
                    ? `${toJalali({ date: props.data.submittedTime, toFormat: "HH:mm" }).string}`
                    : null}
                </Typography>
                <Typography>
                  {props.data.submittedTime
                    ? `${toJalali({ date: props.data.submittedTime, toFormat: "YYYY/MM/DD" }).string}`
                    : null}
                </Typography>
              </Flex>
            </Descriptions.Item>
          </Descriptions>
        </Flex>
      </Flex>
    </Drawer>
  );
};
export default ReportSettlementDetails;
